import { Menu as ChakraMenu, Portal } from '@chakra-ui/react';
import * as React from 'react';

import Icon from './Icon';

interface MenuContentProps extends ChakraMenu.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
}

const MenuContent = React.forwardRef<HTMLDivElement, MenuContentProps>(
  function MenuContent(props, ref) {
    const { portalled = true, portalRef, ...rest } = props;
    return (
      <Portal disabled={!portalled} container={portalRef}>
        <ChakraMenu.Positioner>
          <ChakraMenu.Content
            ref={ref}
            border="1px solid"
            borderColor="gray.400"
            borderRadius="md"
            boxShadow="sm"
            color="gray.800"
            fontWeight="normal"
            minW="225px"
            px={0}
            _open={{ animation: 'none' }}
            _closed={{ animation: 'none' }}
            {...rest}
          />
        </ChakraMenu.Positioner>
      </Portal>
    );
  }
);

const MenuSeparator = React.forwardRef<
  HTMLDivElement,
  ChakraMenu.SeparatorProps
>((props, ref) => (
  <ChakraMenu.Separator borderColor="gray.300" my={2} ref={ref} {...props} />
));

export type MenuItemProps = ChakraMenu.ItemProps & {
  isDanger?: boolean;
};

const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(
  ({ isDanger = false, ...rest }, ref) => (
    <ChakraMenu.Item
      borderRadius="none"
      cursor="pointer"
      _disabled={{ cursor: 'not-allowed' }}
      px={3}
      ref={ref}
      {...(isDanger
        ? {
            color: 'red.600',
            _highlighted: { background: 'red.100' },
          }
        : {
            color: 'gray.800',
            _highlighted: { background: 'gray.100' },
          })}
      {...rest}
    />
  )
);

const MenuCheckboxItem = React.forwardRef<
  HTMLDivElement,
  ChakraMenu.CheckboxItemProps
>(function MenuCheckboxItem(props, ref) {
  return (
    <ChakraMenu.CheckboxItem
      color="gray.800"
      cursor="pointer"
      _highlighted={{ background: 'gray.100' }}
      _disabled={{ cursor: 'not-allowed' }}
      pr={3}
      ref={ref}
      {...props}
    >
      <ChakraMenu.ItemIndicator
        visibility={props.checked ? 'visible' : 'hidden'}
      >
        <Icon icon="check" fontSize="xs" pl={1} />
      </ChakraMenu.ItemIndicator>
      {props.children}
    </ChakraMenu.CheckboxItem>
  );
});

const MenuItemGroup = React.forwardRef<
  HTMLDivElement,
  ChakraMenu.ItemGroupProps
>(function MenuItemGroup(props, ref) {
  const { title, children, ...rest } = props;
  return (
    <ChakraMenu.ItemGroup ref={ref} {...rest}>
      {title && (
        <ChakraMenu.ItemGroupLabel fontSize="xs">
          {title}
        </ChakraMenu.ItemGroupLabel>
      )}
      {children}
    </ChakraMenu.ItemGroup>
  );
});

interface MenuTriggerItemProps extends ChakraMenu.ItemProps {
  startIcon?: React.ReactNode;
}

const MenuTriggerItem = React.forwardRef<HTMLDivElement, MenuTriggerItemProps>(
  function MenuTriggerItem(props, ref) {
    const { startIcon, children, ...rest } = props;
    return (
      <ChakraMenu.TriggerItem ref={ref} px={3} {...rest}>
        {startIcon}
        {children}
        <Icon icon="chevron-right" fontSize="xs" marginLeft="auto" />
      </ChakraMenu.TriggerItem>
    );
  }
);

const MenuItemCommand = React.forwardRef<
  HTMLDivElement,
  ChakraMenu.ItemCommandProps
>((props, ref) => (
  <ChakraMenu.ItemCommand
    ref={ref}
    fontFamily="inherit"
    fontSize="xs"
    {...props}
  />
));

type MenuRootProps = ChakraMenu.RootProps & {
  placement?: NonNullable<ChakraMenu.RootProps['positioning']>['placement'];
};

const MenuRoot: React.FC<MenuRootProps> = ({ placement, ...rest }) => (
  <ChakraMenu.Root
    unmountOnExit={false}
    positioning={{ gutter: 0, overflowPadding: 0, placement }}
    {...rest}
  />
);

const MenuTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraMenu.TriggerProps
>((props, ref) => <ChakraMenu.Trigger asChild ref={ref} {...props} />);

const Menu = {
  CheckboxItem: MenuCheckboxItem,
  Content: MenuContent,
  ContextTrigger: ChakraMenu.ContextTrigger,
  Item: MenuItem,
  ItemCommand: MenuItemCommand,
  ItemGroup: MenuItemGroup,
  Root: MenuRoot,
  Separator: MenuSeparator,
  Trigger: MenuTrigger,
  TriggerItem: MenuTriggerItem,
};

export default Menu;
